/**
 * DashboardLayout Component
 *
 * shadcn/ui SidebarProvider + SidebarInset構成によるメインレイアウト
 * AuthenticatedLayoutからの移行（Material-UI → shadcn/ui）
 *
 * Features:
 * - SidebarProvider: デフォルト開状態でSidebar制御
 * - SidebarInset: コンテンツエリア最適化
 * - 動的パンくずリスト: URLベースで階層表示
 * - レスポンシブ対応: Mobile/Tablet/Desktop
 * - GlobalSearchCommand統合 (Cmd+K)
 * - FloatingAIChat統合
 *
 * Design System v3.0準拠
 */

import React from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'

import { AppSidebar } from './AppSidebar'
import { GlobalSearchCommand } from './GlobalSearchCommand'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'

// パンくずリスト用のパス情報
const pathTitles: Record<string, string> = {
  '/dashboard': 'ダッシュボード',
  '/reservations': '予約管理',
  '/reservations/calendar': 'カレンダー',
  '/customers': '顧客管理',
  '/customers/records': 'カルテ管理',
  '/staff': 'スタッフ管理',
  '/sales': '売上分析',
  '/organization/settings': '組織設定',
  '/settings': '設定',
  '/help': 'ヘルプ',
  '/ai-chat': 'AI相談',
}

// パンくずリストアイテムの型定義
interface BreadcrumbItem {
  title: string
  href?: string
  current?: boolean
}

export function DashboardLayout() {
  const location = useLocation()

  // 現在のパスからパンくずリストを生成
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const path = location.pathname

    // トップレベルページの判定（パンくず1つのみ）
    const topLevelPages = [
      '/dashboard',
      '/customers',
      '/reservations',
      '/staff',
      '/sales',
      '/settings',
      '/organization/settings',
      '/help',
    ]

    if (topLevelPages.includes(path)) {
      const title = pathTitles[path] || 'ページ'
      return [{ title, current: true }]
    }

    // 詳細ページ（例: /customers/123）の場合
    if (path.includes('/customers/') && path.split('/').length === 3 && !path.endsWith('/records')) {
      return [
        { title: 'ダッシュボード', href: '/dashboard' },
        { title: '顧客管理', href: '/customers' },
        { title: '顧客詳細', current: true },
      ]
    }

    if (path.includes('/reservations/') && path.split('/').length === 3 && !path.endsWith('/calendar')) {
      return [
        { title: 'ダッシュボード', href: '/dashboard' },
        { title: '予約管理', href: '/reservations' },
        { title: '予約詳細', current: true },
      ]
    }

    if (path.includes('/staff/') && path.split('/').length === 3) {
      return [
        { title: 'ダッシュボード', href: '/dashboard' },
        { title: 'スタッフ管理', href: '/staff' },
        { title: 'スタッフ詳細', current: true },
      ]
    }

    // 通常のパス
    const segments = path.split('/').filter(Boolean)
    if (segments.length === 0) {
      return [{ title: 'ダッシュボード', current: true }]
    }

    const breadcrumbs: BreadcrumbItem[] = [{ title: 'ダッシュボード', href: '/dashboard' }]

    let currentPath = ''
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`
      const title = pathTitles[currentPath] || segment
      const isLast = index === segments.length - 1

      const item: BreadcrumbItem = {
        title,
        current: isLast,
      }

      if (!isLast) {
        item.href = currentPath
      }

      breadcrumbs.push(item)
    })

    return breadcrumbs
  }

  const breadcrumbs = getBreadcrumbs()

  return (
    <SidebarProvider defaultOpen={true}>
      <AppSidebar />
      <SidebarInset>
        {/* Header with Breadcrumb */}
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <BreadcrumbSeparator />}
                    <BreadcrumbItem>
                      {crumb.current ? (
                        <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
                      ) : crumb.href ? (
                        <BreadcrumbLink href={crumb.href}>
                          {crumb.title}
                        </BreadcrumbLink>
                      ) : (
                        <span>{crumb.title}</span>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <GlobalSearchCommand />
          <Outlet />
        </div>
      </SidebarInset>
      <Toaster position="top-center" richColors />
    </SidebarProvider>
  )
}

export default DashboardLayout
